/** Titans drawn (DESIGN.md: titans' footprints; the user: "fancy IK
 *  legs"), on a 2D canvas over the map: a solid armoured hull over the
 *  footprint (plates seamed hex by hex, lit from the upper left), a turret
 *  with its gun, and each leg a tapered two-bone limb from its hip to a
 *  planted foot, solved by inverse kinematics. Feet stay planted while the
 *  body moves and step when it's carried them too far, in two alternating
 *  groups, lifting as they go. Drills end in a bit, freight legs in a pod,
 *  rail legs in a bogie. Everything casts a shadow. */

const SQRT3 = Math.sqrt(3);

export interface Footprint {
  tiles: [number, number][];
  legs: { at: [number, number]; kind: string }[];
}

type RGB = readonly [number, number, number];

interface Foot {
  x: number;
  y: number;
  /** A step under way: from, to, and how far along (0..1). */
  step: { fx: number; fy: number; tx: number; ty: number; t: number } | null;
}

/** Hex offset to world units (hexes of circumradius 1, pointy-top). */
const offset = (q: number, r: number): [number, number] => [SQRT3 * (q + r / 2), 1.5 * r];

/** Neighbour directions matching the edges between corners k and k+1. */
const EDGE_DIRS: [number, number][] = [
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [0, -1],
  [1, -1],
];

const STEP_SECONDS = 0.4;
/** A foot steps once the body has carried it this far from where it rests. */
const STRIDE = 1.3;
/** How far beyond the body's edge a foot rests. */
const SPLAY = 1.2;
/** The light comes from the upper left; shadows fall down and right. */
const SHADOW: [number, number] = [0.35, 0.55];

const css = (c: RGB, k = 1, a = 1) => `rgba(${Math.round(c[0] * 255 * k)},${Math.round(c[1] * 255 * k)},${Math.round(c[2] * 255 * k)},${a})`;
/** Gunmetal, tinted toward the faction's colour. */
const metal = (team: RGB, share: number): RGB => {
  const base: RGB = [0.2, 0.21, 0.23];
  return [base[0] + (team[0] - base[0]) * share, base[1] + (team[1] - base[1]) * share, base[2] + (team[2] - base[2]) * share];
};

/** A titan's body as drawn: eased toward where the sim has it, its hull
 *  and turret turning at their own rates. */
interface Body {
  x: number;
  y: number;
  /** Hull heading and turret bearing (radians), and speed (world units a
   *  second, smoothed). */
  hull: number;
  turret: number;
  speed: number;
  /** Where it was last frame (for its velocity). */
  px: number;
  py: number;
}

/** Turning rates, radians a second. */
const HULL_TURN = 0.9;
const TURRET_TURN = 1.4;

/** `from` turned toward `to` by at most `step` (shortest way round). */
const turn = (from: number, to: number, step: number) => {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return from + Math.max(-step, Math.min(step, d));
};

export class TitanRig {
  private feet = new Map<number, Foot[]>();
  private bodies = new Map<number, Body>();
  private last = 0;

  /** Draws unit `id` (a titan of footprint `fp`) where the sim has it,
   *  (x, y), aiming at `aim` (a world point) if it has a target, onto
   *  `ctx` (already in world units). */
  draw(ctx: CanvasRenderingContext2D, id: number, fp: Footprint, x: number, y: number, aim: [number, number] | null, team: RGB, time: number, px: number): void {
    const dt = Math.min(0.1, Math.max(0, time - this.last));
    // Limbs in proportion to the hull: a bigger titan, heavier legs.
    const bulk = 0.7 + Math.sqrt(fp.tiles.length) * 0.17;
    // The body: eased toward the sim's position (it moves hex by hex; the
    // body glides), the hull turning to face its way, the turret its aim.
    let b = this.bodies.get(id);
    if (!b) {
      b = { x, y, hull: -Math.PI / 2, turret: -Math.PI / 2, speed: 0, px: x, py: y };
      this.bodies.set(id, b);
    }
    const ease = 1 - Math.exp(-dt * 3);
    b.px = b.x;
    b.py = b.y;
    b.x += (x - b.x) * ease;
    b.y += (y - b.y) * ease;
    const [vx, vy] = dt > 0 ? [(b.x - b.px) / dt, (b.y - b.py) / dt] : [0, 0];
    b.speed += (Math.hypot(vx, vy) - b.speed) * Math.min(1, dt * 4);
    if (b.speed > 0.05) b.hull = turn(b.hull, Math.atan2(vy, vx), HULL_TURN * dt);
    const aimAt = aim ? Math.atan2(aim[1] - b.y, aim[0] - b.x) : b.hull;
    b.turret = turn(b.turret, aimAt, TURRET_TURN * dt);
    const [cx0, cy0, th] = [b.x, b.y, b.hull];
    const [cos, sin] = [Math.cos(th), Math.sin(th)];
    /** A point in the hull's frame, in the world. */
    const world = (lx: number, ly: number): [number, number] => [cx0 + lx * cos - ly * sin, cy0 + lx * sin + ly * cos];
    const [hx, hy] = b.speed > 0.05 ? [vx / Math.max(b.speed, 1e-6), vy / Math.max(b.speed, 1e-6)] : [0, 0];

    let feet = this.feet.get(id);
    if (!feet || feet.length !== fp.legs.length) {
      feet = fp.legs.map((leg) => {
        const [rx, ry] = this.rest(leg.at, fp, bulk);
        const [fx, fy] = world(rx, ry);
        return { x: fx, y: fy, step: null };
      });
      this.feet.set(id, feet);
    }
    // The gait: legs in order round the hull; each steps on its own when
    // it's fallen a stride behind where it'd rest, never beside a stepping
    // neighbour, a third of them at most at once, the furthest behind
    // first. Faster, longer strides and quicker steps.
    const n = fp.legs.length;
    const order = fp.legs.map((leg, k) => [k, Math.atan2(...(offset(leg.at[0], leg.at[1]).reverse() as [number, number]))] as [number, number]).sort((a, c) => a[1] - c[1]).map(([k]) => k);
    const ring = new Map(order.map((k, i) => [k, i]));
    const neighbours = (k: number) => {
      const i = ring.get(k)!;
      return [order[(i + 1) % n], order[(i + n - 1) % n]];
    };
    const stride = STRIDE * bulk * (1 + Math.min(1.5, b.speed * 0.4));
    const stepTime = STEP_SECONDS / (1 + Math.min(1.5, b.speed * 0.3));
    const targets = fp.legs.map((leg) => {
      const [rx, ry] = this.rest(leg.at, fp, bulk);
      const [tx, ty] = world(rx, ry);
      return [tx + hx * stride * 0.5, ty + hy * stride * 0.5] as [number, number];
    });
    for (const foot of feet) {
      if (!foot.step) continue;
      foot.step.t = Math.min(1, foot.step.t + dt / stepTime);
      const e = foot.step.t < 0.5 ? 2 * foot.step.t * foot.step.t : 1 - 2 * (1 - foot.step.t) ** 2;
      foot.x = foot.step.fx + (foot.step.tx - foot.step.fx) * e;
      foot.y = foot.step.fy + (foot.step.ty - foot.step.fy) * e;
      if (foot.step.t >= 1) foot.step = null;
    }
    const behind = feet
      .map((f, k) => [k, Math.hypot(f.x - targets[k][0], f.y - targets[k][1])] as [number, number])
      .filter(([k, d]) => !feet![k].step && d > (fp.legs[k].kind === "Walker" ? stride : stride * 2))
      .sort((a, c) => c[1] - a[1]);
    for (const [k] of behind) {
      const moving = feet.filter((f) => f.step).length;
      if (moving >= Math.max(1, Math.floor(n / 3))) break;
      if (neighbours(k).some((j) => feet![j].step)) continue;
      const f = feet[k];
      f.step = { fx: f.x, fy: f.y, tx: targets[k][0], ty: targets[k][1], t: 0 };
    }
    const limbs = fp.legs.map((leg, k) => {
      const foot = feet![k];
      const [hl, hm] = offset(leg.at[0], leg.at[1]).map((v) => v * 0.8);
      const hip = world(hl, hm);
      const [rx, ry] = this.rest(leg.at, fp, bulk);
      // Two bones sized to the leg, the knee bending away from the centre.
      const bone = Math.hypot(rx - hl, ry - hm) * 0.6 + 0.25;
      let dx = foot.x - hip[0];
      let dy = foot.y - hip[1];
      let d = Math.hypot(dx, dy);
      const reach = 2 * bone - 0.01;
      if (d > reach) {
        foot.x = hip[0] + (dx / d) * reach;
        foot.y = hip[1] + (dy / d) * reach;
        dx = foot.x - hip[0];
        dy = foot.y - hip[1];
        d = reach;
      }
      d = Math.max(d, 0.05);
      const a = d / 2;
      const h = Math.sqrt(Math.max(0, bone * bone - a * a));
      const [ux, uy] = [dx / d, dy / d];
      const k1: [number, number] = [hip[0] + ux * a - uy * h, hip[1] + uy * a + ux * h];
      const k2: [number, number] = [hip[0] + ux * a + uy * h, hip[1] + uy * a - ux * h];
      const knee = Math.hypot(k1[0] - cx0, k1[1] - cy0) > Math.hypot(k2[0] - cx0, k2[1] - cy0) ? k1 : k2;
      const lift = foot.step ? Math.sin(Math.PI * foot.step.t) : 0;
      return { leg, hip, knee, foot: [foot.x, foot.y] as [number, number], lift };
    });

    const hull = metal(team, 0.14);
    const dark = metal(team, 0.06);
    // Shadows first: the legs', then the hull's, falling down and right.
    ctx.fillStyle = "rgba(0,0,0,0.32)";
    ctx.strokeStyle = "rgba(0,0,0,0.32)";
    ctx.lineCap = "round";
    for (const l of limbs) {
      const [sx, sy] = [SHADOW[0] * (1 + l.lift), SHADOW[1] * (1 + l.lift)];
      ctx.lineWidth = 0.42 * bulk;
      ctx.beginPath();
      ctx.moveTo(l.hip[0] + SHADOW[0], l.hip[1] + SHADOW[1]);
      ctx.lineTo(l.knee[0] + SHADOW[0], l.knee[1] + SHADOW[1]);
      ctx.lineTo(l.foot[0] + sx, l.foot[1] + sy);
      ctx.stroke();
    }
    ctx.save();
    ctx.translate(cx0 + SHADOW[0] * 2, cy0 + SHADOW[1] * 2);
    ctx.rotate(th);
    this.hullPath(ctx, fp, 0, 0, 1.02);
    ctx.fill();
    ctx.restore();

    // Legs under the hull: thigh and shin as tapered plates, joints, feet.
    for (const l of limbs) this.limb(ctx, l.hip, l.knee, l.foot, l.lift, l.leg.kind, hull, dark, team, px, bulk);

    // The hull, in its own (turned) frame.
    ctx.save();
    ctx.translate(cx0, cy0);
    ctx.rotate(th);
    this.hullPath(ctx, fp, 0, 0, 1.02);
    ctx.fillStyle = css(hull);
    ctx.fill();
    // Plate seams: each hex's own plate, inset.
    ctx.lineWidth = Math.max(px, 0.04);
    ctx.strokeStyle = css(dark, 0.8, 0.9);
    for (const [q, r] of fp.tiles) {
      const [cx, cy] = offset(q, r);
      this.hexPath(ctx, cx, cy, 0.86);
      ctx.stroke();
    }
    // Detail: vent grilles on the plates round the turret, rivets on the
    // outer ones.
    for (const [q, r] of fp.tiles) {
      const band = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
      const [cx, cy] = offset(q, r);
      if (band === 2) {
        ctx.strokeStyle = css(dark, 0.45, 0.9);
        ctx.lineWidth = 0.09;
        ctx.beginPath();
        for (const t of [-0.3, 0, 0.3]) {
          ctx.moveTo(cx - 0.35, cy + t);
          ctx.lineTo(cx + 0.35, cy + t);
        }
        ctx.stroke();
      } else if (band >= 3) {
        ctx.fillStyle = css(hull, 1.6, 0.8);
        for (let c = 0; c < 6; c += 2) {
          const a = ((60 * c - 30) * Math.PI) / 180;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * 0.62, cy + Math.sin(a) * 0.62, 0.06, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    // Edges: lit facing the light (in the world, so as the hull turns),
    // shaded away from it.
    const set = new Set(fp.tiles.map(([q, r]) => `${q},${r}`));
    for (const [q, r] of fp.tiles) {
      const [cx, cy] = offset(q, r);
      for (let k = 0; k < 6; k++) {
        const [dq, dr] = EDGE_DIRS[k];
        if (set.has(`${q + dq},${r + dr}`)) continue;
        const a0 = ((60 * k - 30) * Math.PI) / 180;
        const a1 = ((60 * k + 30) * Math.PI) / 180;
        const out = (60 * k * Math.PI) / 180 + th;
        const facing = -(Math.cos(out) * 0.6 + Math.sin(out) * 0.8);
        ctx.strokeStyle = facing > 0 ? css(hull, 1.9, 0.9) : css(dark, 0.55, 0.95);
        ctx.lineWidth = Math.max(px * 2, 0.12);
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a0) * 1.02, cy + Math.sin(a0) * 1.02);
        ctx.lineTo(cx + Math.cos(a1) * 1.02, cy + Math.sin(a1) * 1.02);
        ctx.stroke();
      }
    }
    ctx.restore();

    // The turret: a ring and a dome, its gun on its own bearing.
    const size = Math.min(2.4, 0.9 + Math.sqrt(fp.tiles.length) * 0.25);
    const [gx, gy] = [Math.cos(b.turret), Math.sin(b.turret)];
    const [x0, y0] = [cx0, cy0];
    // Barrel (with its shadow), then the turret over its root.
    const length = size * 3.1;
    ctx.lineCap = "butt";
    ctx.strokeStyle = "rgba(0,0,0,0.3)";
    ctx.lineWidth = size * 0.32;
    ctx.beginPath();
    ctx.moveTo(x0 + SHADOW[0], y0 + SHADOW[1]);
    ctx.lineTo(x0 + gx * length + SHADOW[0], y0 + gy * length + SHADOW[1]);
    ctx.stroke();
    // The barrel: a dark outline round lighter metal, so it reads on the hull.
    ctx.strokeStyle = css(dark, 0.45);
    ctx.lineWidth = size * 0.4;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x0 + gx * length, y0 + gy * length);
    ctx.stroke();
    ctx.strokeStyle = css(hull, 1.55);
    ctx.lineWidth = size * 0.26;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x0 + gx * length, y0 + gy * length);
    ctx.stroke();
    ctx.strokeStyle = css(dark, 0.6);
    ctx.lineWidth = size * 0.4;
    ctx.beginPath();
    ctx.moveTo(x0 + gx * (length - size * 0.35), y0 + gy * (length - size * 0.35));
    ctx.lineTo(x0 + gx * length, y0 + gy * length);
    ctx.stroke();
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath();
    ctx.arc(x0 + SHADOW[0] * 0.6, y0 + SHADOW[1] * 0.6, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = css(metal(team, 0.12), 1.1);
    ctx.beginPath();
    ctx.arc(x0, y0, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = Math.max(px * 2, 0.1);
    ctx.strokeStyle = css(dark, 0.5);
    ctx.stroke();
    // The dome, lit on its upper left, with a hatch toward the gun.
    ctx.fillStyle = css(metal(team, 0.1), 1.45);
    ctx.beginPath();
    ctx.arc(x0 - size * 0.12, y0 - size * 0.15, size * 0.62, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = css(dark, 0.7);
    ctx.beginPath();
    ctx.arc(x0 - gx * size * 0.35, y0 - gy * size * 0.35, size * 0.18, 0, Math.PI * 2);
    ctx.fill();
    // The faction's mark: a band of its colour round the turret.
    ctx.strokeStyle = css(team);
    ctx.lineWidth = size * 0.16;
    ctx.beginPath();
    ctx.arc(x0, y0, size * 0.84, 0, Math.PI * 2);
    ctx.stroke();
  }

  /** Where it's drawn now (its body glides behind the sim's position). */
  drawnAt(id: number): [number, number] | null {
    const b = this.bodies.get(id);
    return b ? [b.x, b.y] : null;
  }

  /** Called once a frame, after every titan's drawn. */
  endFrame(time: number, live: Set<number>): void {
    this.last = time;
    for (const id of this.feet.keys()) if (!live.has(id)) this.feet.delete(id);
    for (const id of this.bodies.keys()) if (!live.has(id)) this.bodies.delete(id);
  }

  /** A leg: tapered thigh and shin, hip and knee joints, and its foot. */
  private limb(ctx: CanvasRenderingContext2D, hip: [number, number], knee: [number, number], foot: [number, number], lift: number, kind: string, hull: RGB, dark: RGB, team: RGB, px: number, bulk: number): void {
    const plate = (a: [number, number], b: [number, number], w0: number, w1: number, fill: string) => {
      const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
      const d = Math.max(1e-6, Math.hypot(dx, dy));
      const [nx, ny] = [-dy / d, dx / d];
      ctx.beginPath();
      ctx.moveTo(a[0] + nx * w0, a[1] + ny * w0);
      ctx.lineTo(b[0] + nx * w1, b[1] + ny * w1);
      ctx.lineTo(b[0] - nx * w1, b[1] - ny * w1);
      ctx.lineTo(a[0] - nx * w0, a[1] - ny * w0);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = Math.max(px * 1.5, 0.05);
      ctx.strokeStyle = css(dark, 0.45);
      ctx.stroke();
    };
    const joint = (p: [number, number], r: number) => {
      ctx.beginPath();
      ctx.arc(p[0], p[1], r, 0, Math.PI * 2);
      ctx.fillStyle = css(dark, 0.8);
      ctx.fill();
      ctx.lineWidth = Math.max(px * 1.5, 0.05);
      ctx.strokeStyle = css(hull, 1.6, 0.8);
      ctx.stroke();
    };
    plate(hip, knee, 0.34 * bulk, 0.24 * bulk, css(hull, 1.1));
    plate(knee, foot, 0.22 * bulk, 0.13 * bulk, css(hull, 0.9));
    // A piston along the thigh.
    ctx.strokeStyle = css(hull, 1.7, 0.7);
    ctx.lineWidth = 0.06 * bulk;
    ctx.beginPath();
    ctx.moveTo(hip[0] + (knee[0] - hip[0]) * 0.15, hip[1] + (knee[1] - hip[1]) * 0.15);
    ctx.lineTo(hip[0] + (knee[0] - hip[0]) * 0.8, hip[1] + (knee[1] - hip[1]) * 0.8);
    ctx.stroke();
    joint(hip, 0.32 * bulk);
    joint(knee, 0.24 * bulk);
    // The foot, larger the higher it's lifted.
    const s = (1 + lift * 0.35) * bulk;
    const [dx, dy] = [foot[0] - knee[0], foot[1] - knee[1]];
    const ang = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(foot[0], foot[1]);
    ctx.rotate(ang);
    ctx.scale(s, s);
    ctx.lineWidth = Math.max(px * 1.5, 0.05);
    ctx.strokeStyle = css(dark, 0.4);
    if (kind === "Drill") {
      // A drill bit: a cone with its flutes.
      ctx.beginPath();
      ctx.moveTo(-0.15, -0.28);
      ctx.lineTo(0.55, 0);
      ctx.lineTo(-0.15, 0.28);
      ctx.closePath();
      ctx.fillStyle = "rgb(196,166,92)";
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0.05, -0.18);
      ctx.lineTo(0.3, 0.08);
      ctx.moveTo(0.05, 0.18);
      ctx.lineTo(0.3, -0.08);
      ctx.stroke();
    } else if (kind === "Freight") {
      // A cargo pod.
      ctx.fillStyle = "rgb(150,96,58)";
      ctx.fillRect(-0.3, -0.38, 0.6, 0.76);
      ctx.strokeRect(-0.3, -0.38, 0.6, 0.76);
      ctx.beginPath();
      ctx.moveTo(-0.3, 0);
      ctx.lineTo(0.3, 0);
      ctx.stroke();
    } else if (kind === "Rail") {
      // A bogie: two wheels on a frame.
      ctx.fillStyle = css(dark, 0.9);
      ctx.fillRect(-0.4, -0.12, 0.8, 0.24);
      for (const wx of [-0.28, 0.28]) {
        ctx.beginPath();
        ctx.arc(wx, 0, 0.17, 0, Math.PI * 2);
        ctx.fillStyle = "rgb(150,150,160)";
        ctx.fill();
        ctx.stroke();
      }
    } else {
      // A walker's broad pad, toes forward.
      ctx.beginPath();
      ctx.ellipse(0.08, 0, 0.36, 0.26, 0, 0, Math.PI * 2);
      ctx.fillStyle = css(hull, 0.75);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      for (const t of [-0.18, 0, 0.18]) {
        ctx.moveTo(0.2, t * 0.9);
        ctx.lineTo(0.48, t * 1.3);
      }
      ctx.lineWidth = Math.max(px * 2.5, 0.07);
      ctx.strokeStyle = css(dark, 0.6);
      ctx.stroke();
      // The faction's colour on the ankle.
      ctx.beginPath();
      ctx.arc(-0.1, 0, 0.09, 0, Math.PI * 2);
      ctx.fillStyle = css(team);
      ctx.fill();
    }
    ctx.restore();
  }

  /** The footprint as one path (each hex, scaled `k`), at (x, y). */
  private hullPath(ctx: CanvasRenderingContext2D, fp: Footprint, x: number, y: number, k: number): void {
    ctx.beginPath();
    for (const [q, r] of fp.tiles) {
      const [cx, cy] = offset(q, r);
      for (let c = 0; c < 6; c++) {
        const a = ((60 * c - 30) * Math.PI) / 180;
        const [vx, vy] = [x + cx + Math.cos(a) * k, y + cy + Math.sin(a) * k];
        if (c === 0) ctx.moveTo(vx, vy);
        else ctx.lineTo(vx, vy);
      }
      ctx.closePath();
    }
  }

  private hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, k: number): void {
    ctx.beginPath();
    for (let c = 0; c < 6; c++) {
      const a = ((60 * c - 30) * Math.PI) / 180;
      const [vx, vy] = [x + Math.cos(a) * k, y + Math.sin(a) * k];
      if (c === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();
  }

  /** Where a leg's foot rests, relative to the body: out along its hip's
   *  direction from the centre, just beyond the body's edge that way. */
  private rest(at: [number, number], fp: Footprint, bulk: number): [number, number] {
    const [x, y] = offset(at[0], at[1]);
    const d = Math.hypot(x, y);
    const [ux, uy] = d > 0.01 ? [x / d, y / d] : [0, 1];
    let edge = 0;
    for (const [q, r] of fp.tiles) {
      const [tx, ty] = offset(q, r);
      edge = Math.max(edge, tx * ux + ty * uy);
    }
    const out = edge + SQRT3 / 2 + SPLAY * bulk;
    return [ux * out, uy * out];
  }
}
